import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { useUpdatePlan } from '../api/useUpdatePlan'
import { MODIFY_PLANS_PAGES_TYPES, ModifyPlansPagesTypes } from 'Types/ModifyPlanPagesTypes'
import { ProductTypesWithoutBundle, PRODUCT_TYPES } from 'Types/ProductTypes'
import { GLOBAL_MODALS } from 'Types/GlobalModal'
import { useRevokeCancelation } from 'Features/accountSettings/api/revokeCancelation'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { useIsProductTrial } from 'Hooks/trial/useIsProductTrial'
import { useGetErrorText } from 'Utils/errorText'
import { OldConfirmationModal } from './OldModal'
import { useNavigate } from 'react-router-dom'
import { useSplit } from 'Hooks/useSplit'
import {
  OneButtonCTAModal,
  useFrontEndEventTrackingService,
} from '@clickcease/cc-client-components-lib'
import { useIsTrialSubscription } from 'Hooks/trial/useIsTrialSubscription'
import {
  CrossSellPricingError,
  ModifyPlanErrorClickHere,
  ModifyPlanErrorDescription,
  ModifyPlanErrorTitle,
  ModifyPlanErrorWrapper,
} from './CrossSellPricingConfirmationModal.styled'

type CrossSellPricingConfirmationModalProps = {
  bundle?: boolean
  onOpenChange: () => void
  pageType: ModifyPlansPagesTypes
  plans: Array<number>
  title: string
  description: string
  notice?: string
  productType?: ProductTypesWithoutBundle
  billingCycleInMonths: number
  noNewProduct?: boolean // no new product => no need to open cross sell modal
  amountToPay?: number | string
  isDowngrade?: boolean // user downgraded and will get money back
}

const NEW_MODAL_KEY = 'CE_MODIFY_CROSS_SALE_MODALS_0324'

export const CrossSellPricingConfirmationModal = ({
  onOpenChange,
  bundle,
  plans,
  title,
  pageType,
  description,
  notice,
  productType,
  billingCycleInMonths,
  noNewProduct,
  amountToPay,
  isDowngrade,
}: CrossSellPricingConfirmationModalProps) => {
  const isNewModal = useSplit(NEW_MODAL_KEY)
  const { t } = useTranslation('pricing')
  const { showToaster, setModalToShow } = useAppStore()
  const { mutate, isLoading } = useUpdatePlan()
  const [errorText, setErrorText] = useState<string | undefined>(undefined)
  const { isProductAllowedTrial } = useIsProductTrial()
  const { data: plansDetails } = useGetPlansDetails()
  const { mutate: revokeCancelation } = useRevokeCancelation()
  const { getErrorText } = useGetErrorText()
  const navigate = useNavigate()
  const { track } = useFrontEndEventTrackingService()
  const isPlanCanceled = !!plansDetails?.cancelled
  const isProductTrialAllowed = isProductAllowedTrial(productType)
  const isTrialSubscription = useIsTrialSubscription()

  const updatePlan = () => {
    setErrorText(undefined)

    if (isPlanCanceled) {
      revokeCancelation()
    }

    mutate(
      { products: plans, hideErrorToaster: true },
      {
        onSuccess: (res) => {
          if (!(res instanceof Error)) {
            track(['segment'], 'Payment', {
              is_cross_sell: 'true',
              billing_cycle: billingCycleInMonths === 1 ? 'Monthly' : 'Yearly',
            })
            onOpenChange()
            showToaster(t('cross-sell.cross_sale_pricing.update_successfully'), 'success')
            navigate('/domain-management')
            if (!noNewProduct && productType === PRODUCT_TYPES.BOT_ZAPPING) {
              setModalToShow(GLOBAL_MODALS.BM_CROSS_SALE)
            } else if (!noNewProduct && productType === PRODUCT_TYPES.PAID_MARKETING) {
              setModalToShow(GLOBAL_MODALS.PM_CROSS_SALE)
            }
          } else {
            setErrorText(getErrorText(res, true) || '')
          }
        },
        onError: (error) => {
          setErrorText(getErrorText(error, true) || '')
        },
      }
    )
  }

  const onClose = () => {
    setErrorText(undefined)
    onOpenChange()
  }

  const confirmButtonText = (() => {
    if (isProductTrialAllowed) {
      return t('cross-sell.cross_sale_pricing.confirmation_modal_cta_trial')
    } else if (isDowngrade) {
      return t('cross-sell.cross_sale_pricing.confirmation_modal_cta_downgrade')
    } else {
      return t('cross-sell.cross_sale_pricing.upgrade_plan')
    }
  })()

  const headerText = (() => {
    if (pageType === 'crossSell' && productType) {
      return t(`cross-sell.cross_sale_pricing.add_product_${productType}`)
    } else {
      if (bundle) {
        return isDowngrade
          ? t('modify_plan.modal_header_downgrade')
          : t('modify_plan.modal_header_upgrade')
      } else if (productType) {
        return isDowngrade
          ? t(`modify_plan.modal_header_downgrade_${productType}`)
          : t(`modify_plan.modal_header_upgrade_${productType}`)
      } else {
        // not supposed to happen, but will cover it anyway
        return t('modify_plan.title')
      }
    }
  })()

  const bodyText = (() => {
    if (pageType === 'crossSell') {
      return t('cross-sell.cross_sale_pricing.confirmation_modal_description_trial', {
        amount: amountToPay,
      })
    } else {
      if (isTrialSubscription) {
        return isDowngrade
          ? t('modify_plan.conformation_modal_body_downgrade_during_trial', {
              amount: amountToPay,
            })
          : t('modify_plan.conformation_modal_body_during_trial', {
              amount: amountToPay,
            })
      } else {
        return isDowngrade
          ? t(`modify_plan.conformation_modal_body_downgrade`, {
              amount: amountToPay,
            })
          : t(`modify_plan.conformation_modal_body`, {
              amount: amountToPay,
            })
      }
    }
  })()

  const pictureType = (() => {
    if (bundle) {
      return 'RocketGrowth'
    } else if (productType === PRODUCT_TYPES.BOT_ZAPPING) {
      return 'GraphsDashboard'
    } else {
      return 'PlatformsDashboard'
    }
  })()

  const renderError = () => {
    // no error - no message
    if (!errorText) return undefined

    return (
      <>
        {pageType === MODIFY_PLANS_PAGES_TYPES.CROSS_SELL ? (
          <CrossSellPricingError>
            {t('cross-sell.cross_sale_pricing.error_updating_plan')}
          </CrossSellPricingError>
        ) : (
          <ModifyPlanErrorWrapper oldModal={!isNewModal}>
            <ModifyPlanErrorTitle oldModal={!isNewModal}>
              {t('modify_plan.plan_modification_failed_title')}
            </ModifyPlanErrorTitle>
            <ModifyPlanErrorDescription>
              {errorText && errorText.length > 0 ? (
                <Trans
                  ns="general"
                  i18nKey={errorText}
                  components={{
                    1: (
                      <ModifyPlanErrorClickHere
                        role={'button'}
                        tabIndex={0}
                        onKeyDown={() => {}}
                        onClick={() => {
                          track(['segment'], `Trial ended modify plan error click here`, {})
                          navigate('/account-settings', {
                            state: { openPaymentMethod: true, origin: 'trialEndedBanner' },
                          })
                        }}
                      />
                    ),
                  }}
                ></Trans>
              ) : (
                t('modify_plan.plan_modification_failed_description')
              )}
            </ModifyPlanErrorDescription>
          </ModifyPlanErrorWrapper>
        )}
      </>
    )
  }

  return isNewModal ? (
    <OneButtonCTAModal
      picture={pictureType}
      header={headerText}
      body={bodyText}
      buttonText={confirmButtonText}
      onButtonClick={updatePlan}
      onClose={onClose}
      error={renderError()}
      noticeText={notice}
    />
  ) : (
    <OldConfirmationModal
      title={title}
      pageType={pageType}
      description={description}
      notice={notice}
      isPlanCanceled={isPlanCanceled}
      error={renderError()}
      isLoading={isLoading}
      isProductTrialAllowed={isProductTrialAllowed}
      onOpenChange={onOpenChange}
      onClose={onClose}
      updatePlan={updatePlan}
    />
  )
}
